<template>
  <div>
    <b-container>
      <validation-observer ref="observer" v-slot="{ handleSubmit }">
        <b-form @submit.stop.prevent="handleSubmit(next)" autocomplete="off">
          <b-card>
            <template #header>
              <h6 class="mb-0">Declaración de Estado de Salud del Riesgo</h6>
            </template>

            <p class="text-muted small mb-3">
              Marque Sí o No. Si alguna respuesta es afirmativa, indique el detalle en la grilla (máximo 4
              padecimientos).
            </p>

            <b-row>
              <b-col md="12" class="mb-3">
                <label class="d-block font-weight-bold">
                  1. ¿Tiene usted actualmente cáncer o antecedentes oncológicos recientes, ya sea porque se encuentra en
                  tratamiento o porque la finalización de su tratamiento ocurrió hace menos de cuatro (4) años (o menos
                  de dos (2) años si fue diagnosticado(a) siendo menor de edad)?
                </label>
                <p class="small text-secondary">
                  Si la respuesta es «No», queda amparado por el derecho al olvido oncológico y no deberá declarar ni
                  informar datos adicionales sobre su historial oncológico.
                </p>
                <RadioComponent
                  :form="form"
                  name="oncologyDeclaration"
                  :options="control.TIPOS_SINO"
                  rules="required"
                />
              </b-col>
            </b-row>

            <b-row>
              <b-col md="12" class="mb-3">
                <label class="d-block font-weight-bold">
                  2. ¿Padece o ha padecido alguna de las siguientes enfermedades?
                </label>
                <p class="small text-secondary">
                  Cardiovasculares, cerebrovasculares, renales, pulmonares, neurológicas, gastrointestinales, diabetes,
                  de tiroides, VIH/Sida, Presión Arterial Alta, Lupus, drogadicción o alcoholismo.
                </p>
                <RadioComponent :form="form" name="illnessDeclaration" :options="control.TIPOS_SINO" rules="required" />
              </b-col>
            </b-row>

            <b-row>
              <b-col md="12" class="mb-3">
                <label class="d-block font-weight-bold">
                  3. ¿Padece alguna incapacidad, defecto físico o mental o cualquier otra incapacidad permanente o
                  parcial diferente a los mencionados en el numeral anterior?
                </label>
                <RadioComponent
                  :form="form"
                  name="incapacityDeclaration"
                  :options="control.TIPOS_SINO"
                  rules="required"
                />
              </b-col>
            </b-row>

            <div v-if="muestraGrillaPadecimientos">
              <p v-if="multiplesPreguntasSi" class="small text-muted mb-2">
                Indique a qué pregunta corresponde cada fila.
              </p>
              <div class="table-responsive">
                <table class="table-one tabla-padecimientos">
                  <thead>
                    <tr>
                      <th :class="{ 'pregunta-col': true, 'd-none': !multiplesPreguntasSi }">Corresponde a pregunta</th>
                      <th>Padecimiento / Tratamiento</th>
                      <th>Médico / hospital</th>
                      <th>Fecha inicio</th>
                      <th>Fecha fin</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(item, index) in form.padecimientos" :key="index">
                      <td style="display: none">
                        <span>Padecimiento {{ index + 1 }}</span>
                      </td>
                      <td :class="{ 'pregunta-col': true, 'd-none': !multiplesPreguntasSi }" style="width: 13%">
                        <b-form-select
                          v-model="item.pregunta"
                          :options="opcionesPregunta"
                          :state="getValidationPregunta(index)"
                        />
                      </td>
                      <td style="width: 30%">
                        <b-form-input
                          v-model="item.tratamiento"
                          maxLength="60"
                          :state="getValidationTratamiento(index)"
                        />
                      </td>
                      <td style="width: 30%">
                        <b-form-input
                          v-model="item.medicoHospital"
                          maxLength="40"
                          :state="getValidationMedicoHospital(index)"
                        />
                      </td>
                      <td style="width: 10%">
                        <b-form-input
                          v-model="item.fechaInicio"
                          name="fechaInicio"
                          type="date"
                          :state="getValidationFechaInicio(index)"
                        />
                      </td>
                      <td style="width: 10%">
                        <b-form-input
                          v-model="item.fechaFin"
                          name="fechaFin"
                          type="date"
                          :state="getValidationFechaFin(index)"
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <b-alert v-if="form.padecimientos && form.padecimientos.length > 4" show variant="danger">
              No puede haber más de 4 padecimientos en total.
            </b-alert>

            <b-row align-h="end" class="mt-4">
              <b-col md="4" cols="4" style="display: flex; justify-content: flex-end">
                <b-button @click="previous" variant="secondary">Anterior</b-button>
                <b-button type="submit" variant="primary">Siguiente</b-button>
              </b-col>
            </b-row>
          </b-card>
        </b-form>
      </validation-observer>
    </b-container>
  </div>
</template>

<script>
import RadioComponent from "@/components/controls/RadioComponent.vue";
import { TIPOS_SINO } from "@/data/colombia/vida/Selects";
import { isValidDate, isValidDateBeforeToday } from "@/helpers/dateHelper";

const PADECIMIENTO_VACIO = (preguntaDefault = "") => ({
  pregunta: preguntaDefault,
  tratamiento: "",
  medicoHospital: "",
  fechaInicio: "",
  fechaFin: "",
});

const OPCION_PREGUNTA_1 = { value: "1", text: "1 (Cáncer / oncológicos)" };
const OPCION_PREGUNTA_2 = { value: "2", text: "2 (Enfermedades)" };
const OPCION_PREGUNTA_3 = { value: "3", text: "3 (Incapacidad)" };
const CANTIDAD_FILAS_DEFAULT = 4;

export default {
  name: "FormColombia03",
  components: { RadioComponent },
  data() {
    return {
      form: {},
      control: { TIPOS_SINO },
    };
  },
  computed: {
    muestraGrillaPadecimientos() {
      return (
        this.form.oncologyDeclaration === "S" ||
        this.form.illnessDeclaration === "S" ||
        this.form.incapacityDeclaration === "S"
      );
    },
    multiplesPreguntasSi() {
      const si = [
        this.form.oncologyDeclaration === "S",
        this.form.illnessDeclaration === "S",
        this.form.incapacityDeclaration === "S",
      ].filter(Boolean).length;
      return si > 1;
    },
    opcionesPregunta() {
      const opts = [];
      if (this.form.oncologyDeclaration === "S") opts.push(OPCION_PREGUNTA_1);
      if (this.form.illnessDeclaration === "S") opts.push(OPCION_PREGUNTA_2);
      if (this.form.incapacityDeclaration === "S") opts.push(OPCION_PREGUNTA_3);
      return opts;
    },
  },
  watch: {
    "form.oncologyDeclaration"() {
      this.asegurarGrillaCuatroFilas();
    },
    "form.illnessDeclaration"() {
      this.asegurarGrillaCuatroFilas();
    },
    "form.incapacityDeclaration"() {
      this.asegurarGrillaCuatroFilas();
    },
  },
  mounted() {
    this.$store.dispatch("app/loading", true);
    const { form } = this.$store.state.wizard;

    if (form === undefined || form === null || form === "") {
      this.$router.push("/form/colombia/vida/");
      return;
    }
    this.form = form;
    if (!Array.isArray(this.form.padecimientos)) {
      this.form.padecimientos = [];
    }
    this.asegurarGrillaCuatroFilas();
    this.$store.dispatch("app/loading", false);
  },

  methods: {
    asegurarGrillaCuatroFilas() {
      if (!this.muestraGrillaPadecimientos) {
        this.form.padecimientos = [];
        return;
      }

      if (!this.form.padecimientos) this.form.padecimientos = [];
      const list = this.form.padecimientos;

      let preguntaDefault = "";
      const siOncology = this.form.oncologyDeclaration === "S";
      const siIllness = this.form.illnessDeclaration === "S";
      const siIncapacity = this.form.incapacityDeclaration === "S";
      const cantSi = [siOncology, siIllness, siIncapacity].filter(Boolean).length;

      if (cantSi === 1) {
        if (siOncology) preguntaDefault = "1";
        else if (siIllness) preguntaDefault = "2";
        else if (siIncapacity) preguntaDefault = "3";
      }

      while (list.length < CANTIDAD_FILAS_DEFAULT) {
        list.push(PADECIMIENTO_VACIO(preguntaDefault));
      }

      if (preguntaDefault && list.length > 0) {
        list.forEach((p) => {
          if (!p.pregunta) p.pregunta = preguntaDefault;
        });
      }
    },
    previous() {
      this.$store
        .dispatch("wizard/save", { index: 2, form: this.form })
        .then(() => this.$router.push("/form/colombia-vida-2.html"));
    },
    getValidationPregunta(index) {
      const state = this.rowEmpty(index) || this.form.padecimientos[index].pregunta != "";
      if (state && this.form.padecimientos[index].pregunta == "") {
        return null;
      } else {
        return state;
      }
    },
    getValidationTratamiento(index) {
      const state = this.rowEmpty(index) || this.form.padecimientos[index].tratamiento != "";
      if (state && this.form.padecimientos[index].tratamiento == "") {
        return null;
      } else {
        return state;
      }
    },
    getValidationMedicoHospital(index) {
      const state = this.rowEmpty(index) || this.form.padecimientos[index].medicoHospital != "";
      if (state && this.form.padecimientos[index].medicoHospital == "") {
        return null;
      } else {
        return state;
      }
    },
    getValidationFechaInicio(index) {
      const state =
        this.rowEmpty(index) ||
        (this.form.padecimientos[index].fechaInicio != "" &&
          isValidDateBeforeToday(this.form.padecimientos[index].fechaInicio));
      if (state && this.form.padecimientos[index].fechaInicio == "") {
        return null;
      } else {
        return state;
      }
    },
    getValidationFechaFin(index) {
      if (this.rowEmpty(index)) {
        return null;
      }

      if (this.form.padecimientos[index].fechaFin == "") {
        return true;
      }

      if (this.form.padecimientos[index].fechaFin != "" && !isValidDate(this.form.padecimientos[index].fechaFin)) {
        return false;
      }

      if (
        this.form.padecimientos[index].fechaFin != "" &&
        this.form.padecimientos[index].fechaInicio > this.form.padecimientos[index].fechaFin
      ) {
        return false;
      }

      return true;
    },
    next() {
      const list = this.form.padecimientos || [];

      if (this.form.oncologyDeclaration === "S") {
        const conPregunta1 = list.filter((p) => p.pregunta === "1");
        const algunoP1ConDatos = conPregunta1.some((p) => this.padecimientoConDatos(p));
        if (!algunoP1ConDatos) {
          this.$bvToast.toast(
            "Si respondió Sí a la pregunta 1, indique al menos un detalle " +
              "para una fila correspondiente a la pregunta 1.",
            { title: "Validación", variant: "warning", solid: true }
          );
          return;
        }
      }
      if (this.form.illnessDeclaration === "S") {
        const conPregunta2 = list.filter((p) => p.pregunta === "2");
        const algunoP2ConDatos = conPregunta2.some((p) => this.padecimientoConDatos(p));
        if (!algunoP2ConDatos) {
          this.$bvToast.toast(
            "Si respondió Sí a la pregunta 2, indique al menos un detalle " +
              "para una fila correspondiente a la pregunta 2.",
            { title: "Validación", variant: "warning", solid: true }
          );
          return;
        }
      }
      if (this.form.incapacityDeclaration === "S") {
        const conPregunta3 = list.filter((p) => p.pregunta === "3");
        const algunoP3ConDatos = conPregunta3.some((p) => this.padecimientoConDatos(p));
        if (!algunoP3ConDatos) {
          this.$bvToast.toast(
            "Si respondió Sí a la pregunta 3, indique al menos un detalle " +
              "para una fila correspondiente a la pregunta 3.",
            { title: "Validación", variant: "warning", solid: true }
          );
          return;
        }
      }

      if (!this.validation()) {
        return;
      }

      this.$store
        .dispatch("wizard/save", { index: 4, form: this.form })
        .then(() => this.$router.push("/form/colombia-vida-4.html"));
    },
    tableEmpty() {
      for (let i = 0; i < this.form.padecimientos.length; i++) {
        if (!this.rowEmpty(i)) {
          return false;
        }
      }
      return true;
    },
    rowEmpty(index) {
      return (
        this.form.padecimientos[index].tratamiento == "" &&
        this.form.padecimientos[index].medicoHospital == "" &&
        this.form.padecimientos[index].fechaInicio == "" &&
        this.form.padecimientos[index].fechaFin == ""
      );
    },
    validation() {
      let ok = true;

      for (let i = 0; i < this.form.padecimientos.length; i++) {
        if (!this.rowEmpty(i)) {
          ok =
            this.getValidationPregunta(i) &&
            this.getValidationTratamiento(i) &&
            this.getValidationMedicoHospital(i) &&
            this.getValidationFechaInicio(i) &&
            this.getValidationFechaFin(i);
        }
      }
      return ok;
    },
    padecimientoConDatos(p) {
      return (
        p.tratamiento.trim() != "" ||
        p.medicoHospital.trim() != "" ||
        p.fechaInicio.trim() != "" ||
        p.fechaFin.trim() != ""
      );
    },
  },
};
</script>

<style scoped>
.container {
  max-width: 1600px;
}

.tabla-padecimientos td {
  padding: 0.35rem;
  vertical-align: middle;
}

@media only screen and (max-width: 1000px) {
  .tabla-padecimientos,
  .tabla-padecimientos thead,
  .tabla-padecimientos tbody,
  .tabla-padecimientos th,
  .tabla-padecimientos td,
  .tabla-padecimientos tr {
    display: block;
    position: relative;
  }

  .tabla-padecimientos thead tr {
    display: none;
  }

  .tabla-padecimientos tbody tr {
    margin-bottom: 40px;
  }

  .tabla-padecimientos td {
    border-bottom: 1px solid #eee;
    width: 100% !important;
  }

  .tabla-padecimientos td:has(input, select) {
    padding-top: 30px;
  }

  .tabla-padecimientos td::before {
    position: absolute;
    top: 3px;
    left: 6px;
    padding-right: 10px;
    white-space: nowrap;
    font-weight: bold;
  }

  .tabla-padecimientos td:nth-of-type(1) {
    display: block !important;
    border-bottom: 2px solid var(--primary);
    margin-bottom: 20px;
  }

  .tabla-padecimientos td:nth-of-type(2)::before {
    content: "Corresponde a pregunta";
  }

  .tabla-padecimientos td:nth-of-type(3)::before {
    content: "Padecimiento / Tratamiento";
  }

  .tabla-padecimientos td:nth-of-type(4)::before {
    content: "Médico / hospital";
  }

  .tabla-padecimientos td:nth-of-type(5)::before {
    content: "Fecha inicio";
  }

  .tabla-padecimientos td:nth-of-type(6)::before {
    content: "Fecha fin";
  }
}
</style>
